import { Injectable, NgZone } from '@angular/core'
import { Workspace } from '@app/modules/workspaces/interfaces/workspace.interface'
import { TransformedV3User } from '@app/v2/shared/transformers/user-transformer.service'
import { environment } from '@environments/environment'
import posthog, { JsonType, PostHog } from 'posthog-js'

@Injectable({
  providedIn: 'root'
})
export class PosthogService {
  private _posthog: PostHog

  constructor(private _ngZone: NgZone) {
    if (!environment.production) return
    this._initPostHog()
  }

  private _initPostHog(): void {
    this._ngZone.runOutsideAngular(() => {
      this._posthog = posthog.init(environment.posthog.apiKey, {
        api_host: environment.posthog.host
      })
    })
  }

  /**
   * Sets the user in posthog
   * @param {TransformedV3User} user
   */
  public setUser(user: TransformedV3User): void {
    this._posthog?.identify(user.id.toString(), {
      email: user.email,
      name: user.full_name,
      avatar: user.profile_picture,
      isSuperUser: user.is_admin
    })
  }

  /**
   * Sets the workspace in posthog
   * @param {Workspace} workspace
   * @param {string} role
   */
  public setWorkspace(workspace: Workspace, role: string): void {
    const planId = workspace?.parent_workspace?.plan_id || workspace?.plan_id
    const planName = planId === 2 ? 'Pro' : planId === 1 ? 'Basic' : 'N/A'
    this._posthog?.register({
      workspace_id: workspace?.id,
      workspace_name: workspace?.name,
      workspace_slug: workspace?.slug,
      plan: planName,
      workspace_role: role
    })
  }

  /**
   * Resets the session in posthog
   */
  public resetSession(): void {
    this._posthog?.reset()
  }

  /**
   * Gets the session replay URL
   * @param {number} secondsLookBack
   * @returns {string}
   */
  public getSessionReplayURL(secondsLookBack: number = 10): string {
    return `${environment.posthog?.apiProjectUrl}replay/${this._posthog?.get_distinct_id()}?timestamplookback=${secondsLookBack}`
  }

  /**
   * Gets the feature flag payload
   * @param {string} featureFlag
   * @returns {JsonType | null}
   */
  public getFeatureFlagPayload(featureFlag: string): JsonType | null {
    if (!featureFlag) return null
    return this._posthog?.getFeatureFlagPayload(featureFlag)
  }
}
